import { apiClient } from '../../../api/client'
import type {
  ActionDashboard,
  ActionSelectionKeys,
  DashboardActionsPage,
  DashboardManager,
  DashboardQuery,
  DashboardScope,
  MyActionSelection,
  MyActionsPage,
  MyReport,
  PriorityCriteria,
  ReminderResult,
} from '../types'

const BASE = '/management/actions/dashboard'

/**
 * Dönem + (özel aralıkta) tarih parametreleri — iki uç da **aynı** sözleşmeyi kullanır, böylece iki
 * pano arasında geçiş yapıldığında filtre anlamı değişmez.
 */
function periodParams(query: DashboardQuery): Record<string, string> {
  const params: Record<string, string> = { period: query.period }
  if (query.period === 'CUSTOM') {
    if (query.startDate) params.startDate = query.startDate
    if (query.endDate) params.endDate = query.endDate
  }
  return params
}

/**
 * **Yönetici Raporu**'nun tüm sayıları tek istekte (KPI + dağılım + kişi bazlı + kritik/geciken +
 * üretim anı). Tek uç olması bölümler arası tutarlılığı garanti eder — istemci parça parça toplamaz.
 *
 * `employeeIds` çoklu seçimdir; axios diziyi tekrarlı parametreye açar (`employeeIds=a&employeeIds=b`).
 * Boşsa hiç gönderilmez → sunucu kapsamın tamamını uygular.
 */
export async function getActionDashboard(query: DashboardQuery): Promise<ActionDashboard> {
  const params: Record<string, string | string[]> = periodParams(query)
  if (query.employeeIds.length > 0) params.employeeIds = query.employeeIds
  const { data } = await apiClient.get<ActionDashboard>(BASE, { params })
  return data
}

/** **Raporum** — giriş yapan kullanıcının panosu; kişi filtresi yoktur (analiz §2.1). */
export async function getMyReport(query: DashboardQuery): Promise<MyReport> {
  const { data } = await apiClient.get<MyReport>(`${BASE}/my`, { params: periodParams(query) })
  return data
}

/**
 * **Raporum'un "Aksiyon Beklenen Maddeler" tablosu** (Raporum revizyonu §7) — panonun kendi ucundan
 * ayrı, sunucu-taraflı sayfalı liste. Süzgecin üç boyutu da (kapsam · statü · kalan süre) parametre
 * olarak gider; varsayılanı sunucu da bilir, bu yüzden `remaining` boşsa hiç gönderilmez.
 */
export async function getMyReportActions(
  query: DashboardQuery,
  selection: MyActionSelection,
  page: number,
  size: number,
  sort?: string,
): Promise<MyActionsPage> {
  const params: Record<string, string> = {
    ...periodParams(query),
    scope: selection.scope,
    bucket: selection.bucket,
    page: String(page),
    size: String(size),
  }
  if (selection.remaining) params.remaining = selection.remaining
  // Sıralama yoksa parametre HİÇ gönderilmez → sunucu varsayılan düzenini (hedef teslim tarihi ↑) uygular.
  if (sort) params.sort = sort
  const { data } = await apiClient.get<MyActionsPage>(`${BASE}/my/actions`, { params })
  return data
}

/**
 * **"Önceliklendir"** (Raporum revizyonu §7.4): satırdaki aksiyonun sorumlusuna e-posta + mobil +
 * uygulama içi bildirim üretir. Yönetici Raporu'nun {@link remindAction} ucundan ayrıdır çünkü kapsamı
 * farklıdır ("bana atanan ∪ benim oluşturduğum"). Gövde almaz; hedef yoldaki kimliktir.
 */
export async function prioritizeMyAction(actionId: number): Promise<ReminderResult> {
  const { data } = await apiClient.post<ReminderResult>(`${BASE}/my/reminders/${actionId}`)
  return data
}

/**
 * Kişi seçicisinin seçenekleri = kapsam kişileri. "Tüm Yöneticiler" / "Tüm Bağlı Kullanıcılar"
 * seçeneği sunucudan gelmez; istemci listenin başına ekler. Kapsam çözülemezse boş dizi döner
 * (fail-soft) — seçici gösterilmez, pano çalışmaya devam eder.
 */
export async function getDashboardManagers(): Promise<DashboardManager[]> {
  const { data } = await apiClient.get<DashboardManager[]>(`${BASE}/managers`)
  return data
}

/** Kapsam bilgisi (filtre başlığı + görünürlüğü) — yetki mantığı istemciye taşınmaz (spec §K3). */
export async function getDashboardScope(): Promise<DashboardScope> {
  const { data } = await apiClient.get<DashboardScope>(`${BASE}/scope`)
  return data
}

// ── Alt tablo + hatırlatma (077) ────────────────────────────────────────────────────────────────

/**
 * Süzgeç parametreleri; liste ve **toplu hatırlatma** aynı kümeyi hedeflesin diye tek yerden üretilir
 * (077 §K6 — "ekranda gördüğüm küme" ile "hatırlattığım küme" ayrışamaz).
 */
function filterParams(
  query: DashboardQuery,
  selection: ActionSelectionKeys,
): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = periodParams(query)
  if (query.employeeIds.length > 0) params.employeeIds = query.employeeIds
  // Çoklu seçim (2026-09-16): axios diziyi **tekrarlı** parametreye açar
  // (`bucket=PENDING&bucket=CLOSED_LATE`) ve sunucu değerleri BİRLEŞTİRİR. Boş seçim hiç gönderilmez →
  // sunucu "tüm statüler" varsayılanını uygular; iki taraf aynı kuralı bilir.
  if (selection.length > 0) params.bucket = [...selection]
  return params
}

/**
 * **Alt tablo** — kova filtreli, sunucu-taraflı sayfalı liste (077 §K4). Panonun kendi ucundan ayrıdır:
 * dilim tıklaması ve sayfa değişimi donut'u ve kişi grafiğini yeniden çekmez.
 */
export async function getDashboardActions(
  query: DashboardQuery,
  selection: ActionSelectionKeys,
  page: number,
  size: number,
  sort?: string,
): Promise<DashboardActionsPage> {
  const params: Record<string, string | string[]> = {
    ...filterParams(query, selection),
    page: String(page),
    size: String(size),
  }
  // Sıralama yoksa parametre HİÇ gönderilmez → sunucu kendi varsayılan düzenini (termin ↑) uygular.
  if (sort) params.sort = sort
  const { data } = await apiClient.get<DashboardActionsPage>(`${BASE}/actions`, { params })
  return data
}

/** Tek aksiyon için hatırlatma ("Hatırlat"). Gövde almaz; hedef yoldaki kimliktir. */
export async function remindAction(actionId: number): Promise<ReminderResult> {
  const { data } = await apiClient.post<ReminderResult>(`${BASE}/reminders/${actionId}`)
  return data
}

/**
 * **Toplu** önceliklendirme ("Görevleri Önceliklendir", 2026-10-21): filtrenin eşleştirdiği açık
 * görevlerden (görüntülenen sayfa değil) pencerede seçilen ölçütleri karşılayanlar. Süzgeç parametreleri
 * {@link getDashboardActions} ile birebir aynıdır; ölçütler o kümeyi daraltır. Boş ölçüt hiç
 * gönderilmez — ikisi de boşsa sunucu `400` döner, çağıran butonu zaten pasif tutar.
 */
export async function remindAllActions(
  query: DashboardQuery,
  selection: ActionSelectionKeys,
  criteria: PriorityCriteria,
): Promise<ReminderResult> {
  const params: Record<string, string | string[]> = filterParams(query, selection)
  if (criteria.remaining != null) params.remaining = criteria.remaining
  if (criteria.overdue) params.overdue = 'true'
  const { data } = await apiClient.post<ReminderResult>(`${BASE}/reminders`, null, { params })
  return data
}
